import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import moment from 'moment';
import Calendar from './Calendar';
import { useParams } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import {
  FaClock,
  FaPhone,
  FaGlobe,
  FaTools,
  FaInfoCircle,
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaVideo,
  FaMobileAlt,
  FaStar,
  FaRegCalendarCheck,
  FaArrowLeft,
  FaChevronDown
} from "react-icons/fa";
import apiService from '../../services/ApiServices';
import CustomToolbar from './CustomToolbar';
import './calendarStyles.css';
import CustomDayWrapper from './CustomDayWrapper';
import BookingForm from './BookingForm';

const REDIRECT_DELAY = 5;
const CACHE_TTL_MS = 3 * 60 * 1000; // 3-minute TTL for month-wise timeslot cache

const Booking = () => {
  const [timeSlotsData, setTimeSlotsData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateSlots, setSelectedDateSlots] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { schema, usersslug, slug } = useParams();
  const [eventdetails, setEventdetails] = useState({});
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBookingDateTime, setSelectedBookingDateTime] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [mobileTimeSlotsView, setMobileTimeSlotsView] = useState(false); // mobile: hide calendar, show slots
  const [showConfirmation, setShowConfirmation] = useState(false);

  // ── PayU popup-blocked fallback state ──
  const [payuHtml, setPayuHtml] = useState(null);
  const [payuPopupBlocked, setPayuPopupBlocked] = useState(false);
  const payuOpenedRef = useRef(false); // guards against duplicate payment window opens

  const [countdown, setCountdown] = useState(REDIRECT_DELAY);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [currentDisplayMonth, setCurrentDisplayMonth] = useState(() => moment().format('YYYY-MM'));
  const navigate = useNavigate();

  // ── Month-wise timeslot cache ──
  // Cache structure: Map<string, { data: array, timestamp: number }>
  // Key is 'YYYY-MM', value holds the timeSlots array and the time it was cached.
  const timeslotCacheRef = useRef(new Map());
  // Tracks the month key of the latest fetch request to discard stale responses
  // from rapid month navigation.
  const latestFetchMonthRef = useRef(null);
  // Get user data from localStorage (like NavBar.js)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const redirectPath = `/booking/${schema}/${usersslug}/${slug}`;

  useEffect(() => {
    if (!showConfirmation) return;

    setCountdown(REDIRECT_DELAY);

    const intervalId = setInterval(() => {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    const timeoutId = setTimeout(() => {
      navigate(redirectPath);
    }, REDIRECT_DELAY * 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [showConfirmation, navigate, redirectPath]);

  // ── Cache helper: check if a cached entry for a month is still valid ──
  const getCachedTimeSlots = useCallback((monthKey) => {
    const cache = timeslotCacheRef.current;
    if (cache.has(monthKey)) {
      const entry = cache.get(monthKey);
      if (Date.now() - entry.timestamp < CACHE_TTL_MS) {
        return entry.data;
      }
      // Expired – remove stale entry
      cache.delete(monthKey);
    }
    return null;
  }, []);

  // ── Cache helper: store timeslot data for a month ──
  const setCachedTimeSlots = useCallback((monthKey, data) => {
    timeslotCacheRef.current.set(monthKey, {
      data,
      timestamp: Date.now(),
    });
  }, []);

  // ── Cache helper: invalidate a specific month's cache ──
  const invalidateMonthCache = useCallback((monthKey) => {
    timeslotCacheRef.current.delete(monthKey);
  }, []);

  // ── Fetch timeslots with cache-first strategy ──
  // Returns the fetched data or null if the request was superseded by a newer one.
  const fetchTimeSlotsForMonth = useCallback(async (monthKey, startDate, endDate, opts = {}) => {
    const { skipLoadingState = false } = opts;

    // 1. Check the cache first
    const cached = getCachedTimeSlots(monthKey);
    if (cached) {
      setTimeSlotsData(cached);
      setCalendarLoading(false);
      return cached;
    }

    // 2. Mark this as the latest requested month
    latestFetchMonthRef.current = monthKey;

    if (!skipLoadingState) {
      setCalendarLoading(true);
    }

    try {
      const url = `/api/booking-page/timeslot/${schema}/${slug}?inviteeTimezone=Asia/Kolkata&startDate=${startDate}&endDate=${endDate}`;
      const response = await apiService.get(url);

      // 3. Discard if a newer month request has been initiated while this one was in-flight
      if (latestFetchMonthRef.current !== monthKey) {
        return null;
      }

      // 4. Cache the result and update state
      setCachedTimeSlots(monthKey, response.timeSlots);
      setTimeSlotsData(response.timeSlots);
      setCalendarLoading(false);
      return response.timeSlots;
    } catch (e) {
      // Only show error if this is still the latest requested month
      if (latestFetchMonthRef.current === monthKey) {
        setError(e.message);
        setCalendarLoading(false);
      }
      return null;
    }
  }, [schema, slug, getCachedTimeSlots, setCachedTimeSlots]);

  useEffect(() => {
    const today = moment();
    const monthKey = today.format('YYYY-MM');
    const startDate = today.format('YYYY-MM-DD');
    const endDate = today.clone().endOf('month').format('YYYY-MM-DD');

    fetchTimeSlotsForMonth(monthKey, startDate, endDate);

    const fetchEventdetails = async () => {
      try {
        const response = await apiService.get(`/api/booking-page/booking-events/${schema}/${usersslug}/${slug}`);
        if (response?.event) {
          setEventdetails(response.event);
        }
      } catch (e) {
        setError(e.message);
      }
    };

    fetchEventdetails();
  }, [schema, usersslug, slug, fetchTimeSlotsForMonth]);

  const getAvailableDates = () => {
    return timeSlotsData
      ? timeSlotsData
        .filter((slot) => slot.status === 'available' && slot.spots.length > 0)
        .map((slot) => moment(slot.date).toDate())
      : [];
  };

  const handleDateSelect = (date) => {
    if (!date || !availableDates.some((avdate) => moment(avdate).isSame(date, 'day'))) {
      return;
    }

    setSelectedDate(date);
    setSelectedBookingDateTime(null);
    setShowBookingForm(false);
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const slotsForDate = timeSlotsData?.find(
      (slot) => slot.date === formattedDate
    )?.spots;
    setSelectedDateSlots(slotsForDate || []);
    setShowTimeSlots(true);
    // On mobile (<768px) switch to full-screen time slots view
    if (window.innerWidth < 768) {
      setMobileTimeSlotsView(true);
    }
  };

  const handleBackToCalendar = () => {
    setMobileTimeSlotsView(false);
    setShowTimeSlots(false);
    setSelectedBookingDateTime(null);
  };

  const availableDates = getAvailableDates();

  // Get user's first letter for avatar (like NavBar.js)
  const getUserInitial = () => {
    if (userData && userData.name) {
      return userData.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get user's display name (like NavBar.js)
  const getUserName = () => {
    if (userData && userData.name) {
      return userData.name;
    }
    return 'User';
  };

  // Auto-select current date when data loads
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      const currentDate = new Date();
      const todayAvailable = availableDates.find(date =>
        moment(date).isSame(currentDate, 'day')
      );

      // Select today if available, otherwise first available date
      const dateToSelect = todayAvailable || availableDates[0];
      setSelectedDate(dateToSelect);
      const formattedDate = moment(dateToSelect).format('YYYY-MM-DD');
      const slotsForDate = timeSlotsData?.find(
        (slot) => slot.date === formattedDate
      )?.spots;
      setSelectedDateSlots(slotsForDate || []);
      setSelectedBookingDateTime(null);
      // Don't show time slots automatically - only when user clicks
      setShowTimeSlots(false);
    }
  }, [availableDates, timeSlotsData]);

  const handleTimeSlotClick = (slotTime) => {
    setSelectedBookingDateTime(slotTime);
  };

  const handleProceedToBooking = () => {
    if (!selectedBookingDateTime) return;
    setShowBookingForm(true);
  };

  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
    setSelectedBookingDateTime(null);
  };

  // Detect month changes from calendar navigation and fetch new time slots (with caching)
  useEffect(() => {
    if (!selectedDate) return;
    const newMonth = moment(selectedDate).format('YYYY-MM');
    if (newMonth !== currentDisplayMonth) {
      setCurrentDisplayMonth(newMonth);
      const selectedMoment = moment(selectedDate);
      const startDate = selectedMoment.clone().startOf('month').format('YYYY-MM-DD');
      const endDate = selectedMoment.clone().endOf('month').format('YYYY-MM-DD');
      fetchTimeSlotsForMonth(newMonth, startDate, endDate);
    }
  }, [selectedDate, currentDisplayMonth, fetchTimeSlotsForMonth]);

  const handleBookingSubmit = async (bookingData) => {
    if (submitting) return; // Prevent duplicate submissions
    setSubmitting(true);
    try {
      const event_id = bookingData.eventDetails.id;
      const url = `https://api.meetify.co.in/api/booking-page/create/${schema}/${event_id}?inviteeTimezone=Asia/Kolkata`;

      // Use fetch with text response to support both HTML (PayU) and JSON responses
      //const token = localStorage.getItem('AUTH_TOKEN_KEY');
      const fetchResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // ...(token ? { 'x-auth': token } : {}),
        },
        body: JSON.stringify(bookingData),
      });

      if (!fetchResponse.ok) {
        const errorText = await fetchResponse.text();
        let errorMessage = `Booking failed (${fetchResponse.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          // errorText is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const responseText = await fetchResponse.text();
      const contentType = fetchResponse.headers.get('content-type') || '';
      const isHtml = contentType.includes('text/html') || responseText.trim().startsWith('<!doctype') || responseText.trim().startsWith('<html');

      if (isHtml) {
        // Paid event: open PayU payment page in a new tab
        const payuWindow = window.open('', '_blank');
        if (payuWindow) {
          // Popup opened successfully — write PayU HTML directly
          payuWindow.document.open();
          payuWindow.document.write(responseText);
          payuWindow.document.close();
          payuOpenedRef.current = true;
        } else {
          // Popup was blocked — store HTML for fallback "Pay Now" button
          setPayuHtml(responseText);
          setPayuPopupBlocked(true);
          payuOpenedRef.current = false;
        }

        // Invalidate cache and refresh availability
        invalidateMonthCache(currentDisplayMonth);
        const currentMoment = moment(currentDisplayMonth, 'YYYY-MM');
        const refreshStartDate = currentMoment.clone().startOf('month').format('YYYY-MM-DD');
        const refreshEndDate = currentMoment.clone().endOf('month').format('YYYY-MM-DD');
        await fetchTimeSlotsForMonth(currentDisplayMonth, refreshStartDate, refreshEndDate, { skipLoadingState: true });

        // Reset selected date and show confirmation
        setSelectedDate(null);
        setSelectedDateSlots([]);
        setShowTimeSlots(false);
        setConfirmationDetails({
          title: bookingData.eventDetails?.title || eventdetails?.title || 'Meeting',
          guestName: bookingData.guestName,
          guestEmail: bookingData.guestEmail,
          startTime: bookingData.startTime,
          duration: getEventDuration(),
          meetLink: null
        });
        setShowBookingForm(false);
        setSelectedBookingDateTime(null);
        setShowConfirmation(true);
      } else {
        // Free event: parse JSON and handle as before
        const response = JSON.parse(responseText);
        setTimeSlotsData(response.timeSlots);
        // Invalidate the current month's cache so we fetch fresh availability
        invalidateMonthCache(currentDisplayMonth);
        // Refresh the time slots data after successful POST
        const currentMoment = moment(currentDisplayMonth, 'YYYY-MM');
        const refreshStartDate = currentMoment.clone().startOf('month').format('YYYY-MM-DD');
        const refreshEndDate = currentMoment.clone().endOf('month').format('YYYY-MM-DD');
        await fetchTimeSlotsForMonth(currentDisplayMonth, refreshStartDate, refreshEndDate, { skipLoadingState: true });
        // Reset selected date and slots to trigger re-selection
        setSelectedDate(null);
        setSelectedDateSlots([]);
        setShowTimeSlots(false);
        setConfirmationDetails({
          title: bookingData.eventDetails?.title || eventdetails?.title || 'Meeting',
          guestName: bookingData.guestName,
          guestEmail: bookingData.guestEmail,
          startTime: bookingData.startTime,
          duration: getEventDuration(),
          meetLink: response?.meetlink || response?.meeting?.meetlink
        });
        setShowBookingForm(false);
        setSelectedBookingDateTime(null);
        setShowConfirmation(true);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── PayU fallback: open stored payment HTML on user click ──
  const handlePayNowClick = () => {
    if (payuOpenedRef.current || !payuHtml) return; // already opened or no HTML available

    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      // Second popup also blocked — instruct user without losing payment state
      setPayuPopupBlocked(true);
      alert('Your browser is still blocking popups. Please allow popups for this site in your browser settings, then click "Pay Now" again.');
      return;
    }

    // Write the stored PayU HTML into the newly opened window
    newWindow.document.open();
    newWindow.document.write(payuHtml);
    newWindow.document.close();

    // Mark as opened and clear fallback state
    payuOpenedRef.current = true;
    setPayuPopupBlocked(false);
    setPayuHtml(null);
  };

  // Safe date formatting
  const formatDateSafe = (date, format = 'dddd, MMMM D') => {
    if (!date || !moment(date).isValid()) {
      return 'Select a Date';
    }
    return moment(date).format(format);
  };

  // Event duration formatting
  const getEventDuration = () => {
    if (!eventdetails?.duration) return '15 mins';

    try {
      const duration = JSON.parse(eventdetails.duration);
      return duration.normalvalue === 'custom'
        ? duration.customvalue.join(' ')
        : duration.normalvalue;
    } catch {
      return '15 mins';
    }
  };

  // Event type icon
  const getEventTypeIcon = () => {
    const eventType = eventdetails?.event_type?.toLowerCase();
    switch (eventType) {
      case 'video': return <FaVideo className="text-primary" />;
      case 'phone': return <FaPhone className="text-success" />;
      case 'mobile': return <FaMobileAlt className="text-info" />;
      default: return <FaRegCalendarCheck className="text-secondary" />;
    }
  };



  if (error) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger shadow border-0" role="alert">
          <h5 className="alert-heading">
            <FaInfoCircle className="me-2" />
            Unable to Load Booking
          </h5>
          <p className="mb-0">Error fetching data: {error}</p>
        </div>
      </div>
    );
  }

  const currentDateLabel = formatDateSafe(selectedDate);

  return (
    <div className="booking-page-wrapper">
      <div className="booking-card">

        {showConfirmation ? (
          /* ── Confirmation Screen ── */
          <div className="confirmation-screen p-4 p-md-5 text-center">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10 text-success mb-3" style={{ width: '72px', height: '72px' }}>
              <FaRegCalendarCheck size={32} />
            </div>
            <h3 className="fw-bold text-dark mb-2">{eventdetails?.eventSettings?.is_paid === true ? (payuPopupBlocked ? 'Meeting will be confirmed after payment.' : 'Meeting will be confirmed after payment. Complete payment in the opened tab.') : 'Meeting Confirmed'}</h3>
            <p className="text-muted mb-4">
              {
                confirmationDetails?.guestName
                  ? `${confirmationDetails.guestName}, you're booked for ${confirmationDetails.title}.`
                  : `You're booked for ${confirmationDetails?.title || 'your meeting'}.`

              }
              {eventdetails?.eventSettings?.is_default_confirm === false &&
                eventdetails?.eventSettings?.custom_confirm?.trim()
                ? (
                  <span
                    className="custom-confirm-message mt-1"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        eventdetails.eventSettings.custom_confirm,
                        { ALLOWED_TAGS: ['br', 'strong', 'em', 'b', 'i', 'p', 'ul', 'ol', 'li', 'a', 'span'] }
                      )
                    }}
                  />
                ) : null}

            </p>

            <div className="d-flex justify-content-center flex-wrap gap-3 mb-4">
              <div className="border rounded-3 px-4 py-3 text-start shadow-sm bg-light">
                <p className="text-muted small mb-1">Date & Time</p>
                <p className="fw-semibold text-dark mb-0">
                  {confirmationDetails?.startTime ? moment(confirmationDetails.startTime).format('ddd, MMM D, YYYY h:mm A') : 'Scheduled'}
                  {" - "}
                  {confirmationDetails?.duration || getEventDuration()}
                </p>
              </div>
              {/* <div className="border rounded-3 px-4 py-3 text-start shadow-sm bg-light">
                <p className="text-muted small mb-1">Duration</p>
                <p className="fw-semibold text-dark mb-0">{confirmationDetails?.duration || getEventDuration()}</p>
              </div> */}
            </div>

            {confirmationDetails?.guestEmail && (
              <p className="text-muted small mb-4">A confirmation has been sent to {confirmationDetails.guestEmail}.</p>
            )}



            {/* <div className="d-inline-flex align-items-center gap-2 px-3 py-2 bg-light rounded-pill mb-4">
              <span className="badge bg-success fs-6 mb-0">{countdown}s</span>
              <span className="text-muted small">Redirecting shortly...</span>
            </div> */}

            {/* ── PayU popup-blocked fallback: Pay Now button ── */}
            {payuPopupBlocked && payuHtml && (
              <div className="payu-fallback-container mb-4">
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 justify-content-center" role="alert">
                  <FaInfoCircle />
                  <span>Your browser blocked the payment popup. Click the button below to proceed to payment.</span>
                </div>
                <button
                  className="btn btn-lg payu-pay-now-btn"
                  onClick={handlePayNowClick}
                  type="button"
                  id="payu-fallback-pay-now"
                >
                  Pay Now
                </button>
              </div>
            )}

            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a className="btn btn-primary" href={redirectPath}>Book another event</a>
              {confirmationDetails?.meetLink && (
                <a className="btn btn-outline-success" href={confirmationDetails.meetLink} target="_blank" rel="noreferrer">
                  Open meeting link
                </a>
              )}
            </div>

          </div>
        ) : showBookingForm ? (
          /* ── Booking Form (full width) ── */
          <div className="md-4">
            <BookingForm
              selectedDateTime={selectedBookingDateTime}
              eventDetails={eventdetails}
              onClose={handleCloseBookingForm}
              onSubmit={handleBookingSubmit}
            />
          </div>
        ) : (
          /* ── Main Layout ── */
          <div className="booking-layout">

            {/* ── LEFT PANEL: Event Details ── */}
            <div className={`booking-sidebar bg-light${mobileTimeSlotsView ? ' booking-sidebar-hidden' : ''}`}>
              <div className="sidebar-inner">

                <button
                  className="mobile-back-btn"
                  onClick={() => navigate(-1)}
                >
                  <FaArrowLeft />

                </button>

                {/* Mobile: compact horizontal strip */}
                <div style={{ "align-self": "center", "text-align": "center" }}>
                  {/* <div className="avatar-circle bg-primary">
                    <span className="text-white fw-bold">{getUserInitial()}</span>
                  </div> */}
                  <div >
                    {/* <p className="text-muted small mb-0">Meeting with</p> */}
                    <h6 className="text-dark mb-0">{getUserName()}</h6>
                    <h2 className="fw-bold text-dark mb-3">
                      {eventdetails?.title || `Meeting with ${getUserName()}`}
                    </h2>
                  </div>
                </div>


                <div className="event-meta">
                  <div className="event-meta-item">
                    <div className="meta-icon bg-white shadow-sm">
                      <FaClock className="text-primary" style={{ fontSize: '14px' }} />
                    </div>
                    <div>
                      {/* <p className="text-muted small mb-0">Duration</p> */}
                      <p className="text-dark fw-semibold mb-0">{getEventDuration()}</p>
                    </div>
                  </div>

                  {/* <div className="event-meta-item">
                    <div className="meta-icon bg-white shadow-sm">
                      {getEventTypeIcon()}
                    </div>
                    <div>
                      <p className="text-muted small mb-0">Event Type</p>
                      <p className="text-dark fw-semibold mb-0 text-capitalize">{eventdetails?.event_type || 'Meeting'}</p>
                    </div>
                  </div> */}

                  <div className="event-meta-item">
                    <div className="meta-icon bg-white shadow-sm">
                      <FaMapMarkerAlt className="text-info" style={{ fontSize: '14px' }} />
                    </div>
                    <div>
                      {/* <p className="text-muted small mb-0">Location</p> */}
                      <p className="text-dark fw-semibold mb-0">{eventdetails?.locationtype == 'IN_PERSON' ? eventdetails?.location_details : 'Virtual Meeting'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RIGHT PANEL: Calendar + Time Slots ── */}
            <div className="booking-main" style={{ position: 'relative' }}>
              {/* ── Calendar Loading Overlay ── */}
              {calendarLoading && (
                <div className="calendar-loading-overlay">
                  <div className="calendar-loading-spinner">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-dark fw-semibold mt-2 mb-0">Loading availability...</p>
                  </div>
                </div>
              )}
              <div className="p-3 p-md-4">

                {/* ── MOBILE: Time Slots full-screen view ── */}
                {mobileTimeSlotsView ? (
                  <div className="mobile-slots-view animate-slide-in">
                    {/* Mobile back button: circular button with blue arrow icon */}
                    <button
                      className="mobile-back-btn"
                      onClick={handleBackToCalendar}
                      aria-label="Back to Calendar"
                    >
                      <FaArrowLeft />
                    </button>

                    {/* Centered Date Heading */}
                    <div className="mobile-date-header text-center">
                      <h4 className="fw-bold text-dark mb-1">
                        {selectedDate ? moment(selectedDate).format('dddd') : ''}
                      </h4>
                      <p className="text-muted mb-0">
                        {selectedDate ? moment(selectedDate).format('MMMM D, YYYY') : ''}
                      </p>
                    </div>

                    {/* Timezone Section */}
                    <div className="mobile-timezone-container text-center">
                      <div className="mobile-timezone-label">Time zone</div>
                      <div className="mobile-timezone-selector">
                        <FaGlobe />
                        <span>India Standard Time ({moment().format('h:mma')})</span>
                        <FaChevronDown className="chevron-icon" />
                      </div>
                    </div>

                    {/* Select a Time Header */}
                    <div className="text-center mb-4">
                      <h4 className="mobile-select-time-title">Select a Time</h4>
                      <p className="mobile-duration-label">Duration: {getEventDuration()}</p>
                    </div>

                    {/* Time slots list */}
                    <div className="mobile-time-slots-container">
                      <div className="time-slots-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {selectedDate && selectedDateSlots.length > 0 ? (
                          <div className="time-slots-grid">
                            {selectedDateSlots.map((slot, index) => (
                              <div key={index} className="mobile-time-slot-item">
                                <div className="d-flex align-items-center mobile-time-slot-row">
                                  <button
                                    className={`booking-time-slot-btn ${selectedBookingDateTime === slot.start_time ? 'selected' : ''
                                      }`}
                                    onClick={() => handleTimeSlotClick(slot.start_time)}
                                  >
                                    {moment(slot.start_time).format('h:mma')}
                                  </button>
                                  {selectedBookingDateTime === slot.start_time && (
                                    <button
                                      className="booking-next-btn"
                                      onClick={handleProceedToBooking}
                                    >
                                      Next
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <FaCalendarAlt size={40} className="text-muted mb-3 opacity-50" />
                            <h6 className="text-dark mb-1">No Available Slots</h6>
                            <p className="text-muted small mb-0">No time slots available for {currentDateLabel}.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── DESKTOP / default: calendar + side slots ── */
                  <>
                    {/* Header */}
                    <div className="flex justify-center items-center">
                      <h5 className=" text-dark mb-0">Select a Day</h5>

                    </div>

                    {/* Calendar + Side Slots flex row */}
                    <div className="desktop-cal-slots-row">

                      {/* Calendar column */}
                      <div className={`desktop-cal-col${showTimeSlots ? ' shrink' : ''}`}>
                        <div className="calendar-wrapper mb-3">
                          <Calendar
                            selectedDate={selectedDate}
                            Toolbar={(props) => (<CustomToolbar {...props} />)}
                            setSelectedDate={setSelectedDate}
                            dateCellWrapper={(props) => (
                              <CustomDayWrapper
                                {...props}
                                selectedDate={selectedDate}
                                availableDates={availableDates}
                                handleNavigate={handleDateSelect}
                              />
                            )}
                          />
                        </div>

                        {/* Timezone Info */}
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <FaGlobe className="text-muted me-2" />
                                <div>
                                  <p className="text-muted small mb-0">Current Timezone</p>
                                  <p className="text-dark fw-semibold mb-0 small">
                                    India Standard Time (IST) — {moment().format('h:mm A')}
                                  </p>
                                  {/* <p>v</p> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Side time slots column */}
                      {showTimeSlots && (
                        <div className="desktop-slots-col animate-slide-in-right">
                          <div className="card border-0 shadow-sm" style={{ height: '100%' }}>
                            <div className="card-header bg-white border-bottom py-3">
                              <h6 className="fw-bold text-dark mb-0 text-center">
                                <FaClock className="me-2 text-primary" />
                                {currentDateLabel}
                              </h6>
                            </div>
                            <div className="card-body p-0">
                              <div className="time-slots-container" style={{ maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                                {selectedDate && selectedDateSlots.length > 0 ? (
                                  selectedDateSlots.map((slot, index) => (
                                    <div key={index} className="time-slot-item mb-2">
                                      <div className="d-flex align-items-center time-slot-row">
                                        <button
                                          className={`booking-time-slot-btn ${selectedBookingDateTime === slot.start_time ? 'selected' : ''
                                            }`}
                                          onClick={() => handleTimeSlotClick(slot.start_time)}
                                        >
                                          {moment.tz(slot.start_time).format('h:mma')}
                                        </button>
                                        {selectedBookingDateTime === slot.start_time && (
                                          <button
                                            className="booking-next-btn"
                                            onClick={handleProceedToBooking}
                                          >
                                            Next
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center py-4">
                                    <FaCalendarAlt size={40} className="text-muted mb-3 opacity-50" />
                                    <h6 className="text-dark mb-1">No Available Slots</h6>
                                    <p className="text-muted small mb-0">No time slots for {currentDateLabel}.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Booking Page Styles */}
      <style>{`
        /* ── Base layout ── */
        .booking-page-wrapper {
          min-height: 100vh;
          padding: 1rem;
          display: flex;
          align-items: flex-start;
          justify-content: center;
        }

        .booking-card {
          width: 100%;
          max-width: 960px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.10);
          overflow: hidden;
        }

        /* ── Flex layout: sidebar + main ── */
        .booking-layout {
          display: flex;
          flex-direction: column; /* mobile: stack vertically */
        }

        /* ── Sidebar ── */
        .booking-sidebar {
          width: 100%;
          padding: 1.25rem 1rem;
          border-bottom: 1px solid #e9ecef;
        }

        .sidebar-inner {
          display: flex;
          flex-direction: column;
        }

        /* Compact horizontal organizer strip (mobile) */
        .organizer-block {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.25rem;
        }

        .avatar-circle {
          width: 44px;
          height: 44px;
          min-width: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
        }

        .organizer-info { flex: 1; }

        .event-title {
          font-size: 1rem;
          line-height: 1.3;
        }

        /* Event meta items — horizontal on mobile */
        .event-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .event-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 0;
        }

        .meta-icon {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* ── Main booking area ── */
        .booking-main {
          flex: 1;
          min-width: 0;
          min-height:60vh;
          position: relative;
        }

        /* ── Calendar Loading Overlay ── */
        .calendar-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.80);
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: inherit;
          backdrop-filter: blur(2px);
          transition: opacity 0.2s ease;
        }

        .calendar-loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
        }

        .calendar-loading-spinner .spinner-border {
          width: 2.5rem;
          height: 2.5rem;
        }

        .calendar-loading-spinner p {
          font-size: 0.9rem;
          color: #333;
        }

        /* ── Time slots grid (2 columns on small screens) ── */
        .time-slots-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.5rem;
          padding: 0.5rem;
        }

        .time-slot-item {
          transition: all 0.2s ease;
        }

        .time-slot-row {
          gap: 0.4rem;
        }

        .time-slots-container {
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: #bdbdbd #f1f1f1;
        }

        .time-slots-container::-webkit-scrollbar { width: 3px; }
        .time-slots-container::-webkit-scrollbar-thumb { background-color: #bdbdbd; border-radius: 3px; }
        .time-slots-container::-webkit-scrollbar-track { background: #f1f1f1; }

        .time-slots-panel {
          margin-top: 0.5rem;
        }

        /* Hide sidebar on mobile when time-slots view is active */
        .booking-sidebar-hidden {
          display: none !important;
        }

        /* On desktop never hide the sidebar */
        @media (min-width: 768px) {
          .booking-sidebar-hidden {
            display: block !important;
          }
        }

        /* ── Desktop: calendar + slots side-by-side ── */
        .desktop-cal-slots-row {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }

        /* Full width when no slots shown */
        .desktop-cal-col {
          flex: 1 1 100%;
          transition: flex 0.3s ease;
          min-width: 0;
        }

        /* Shrink calendar to ~58% when slots appear */
        .desktop-cal-col.shrink {
          flex: 0 0 58%;
          max-width: 58%;
        }

        /* Slots column slides in from right */
        .desktop-slots-col {
          flex: 0 0 40%;
          max-width: 40%;
          min-width: 160px;
        }

        @keyframes slideInRight {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.35s ease-out forwards;
        }

        /* ── Animations ── */
        @keyframes slideIn {
          from { transform: translateY(12px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-slide-in { animation: slideIn 0.4s ease-out forwards; }

        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .animate-next-appear { animation: fadeSlideIn 0.4s ease-out; }

        /* ── Tablet / Desktop overrides ── */
        @media (min-width: 640px) {
          .time-slots-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }

        @media (min-width: 768px) {
          .booking-page-wrapper {
            padding: 2rem 1rem;
          }

          /* Side-by-side on tablet+ */
          .booking-layout {
            flex-direction: row;
            align-items: stretch;
          }

          .booking-sidebar {
            width: 260px;
            min-width: 220px;
            max-width: 300px;
            border-bottom: none;
            border-right: 1px solid #e9ecef;
            padding: 1.5rem;
          }

          /* Vertical stack for organizer on desktop */
          .organizer-block {
            flex-direction: column;
            text-align: center;
          }

          .event-meta {
            flex-direction: column;
            gap: 1rem;
          }

          .event-title {
            font-size: 1.1rem;
            text-align: center;
          }

          .time-slots-grid {
            grid-template-columns: 1fr 1fr;
          }

          .avatar-circle {
            width: 56px;
            height: 56px;
            font-size: 1.3rem;
          }
        }

        @media (min-width: 992px) {
          .booking-sidebar {
            width: 280px;
          }

          .time-slots-grid {
            grid-template-columns: 1fr;
          }
        }

        /* ── Calendar responsive fixes ── */
        .calendar-wrapper .rbc-calendar {
          min-height: 280px;
        }

        @media (max-width: 480px) {
          .calendar-wrapper .rbc-calendar {
            font-size: 0.8rem;
          }
          .calendar-wrapper .rbc-header {
            padding: 8px 4px;
          }
        }

        /* ── Common Time Slot Styling (Desktop & Mobile) ── */
        .booking-time-slot-btn {
          width: 100%;
          height: 48px;
          background-color: #ffffff !important;
          border: 1px solid #006bff !important;
          color: #006bff !important;
          font-weight: 700 !important;
          font-size: 1rem !important;
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease !important;
          outline: none !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          margin-bottom: 0 !important; /* Override global .btn margin-bottom */
        }

        .booking-time-slot-btn:hover,
        .booking-time-slot-btn:focus {
          background-color: rgba(0, 107, 255, 0.08) !important;
          border-color: #006bff !important;
          color: #006bff !important;
        }

        .booking-time-slot-btn.selected {
          width: 50% !important;
          background-color: #5d5d5d !important;
          border-color: #5d5d5d !important;
          color: #ffffff !important;
        }

        .booking-next-btn {
          width: 50% !important;
          height: 48px;
          background-color: #006bff !important;
          border: 1px solid #006bff !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          font-size: 1rem !important;
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease !important;
          outline: none !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          animation: fadeSlideIn 0.3s ease-out !important;
          margin-bottom: 0 !important; /* Override global .btn margin-bottom */
        }

        .booking-next-btn:hover,
        .booking-next-btn:focus {
          background-color: #0056cc !important;
          border-color: #0056cc !important;
          color: #ffffff !important;
        }

        /* ── Mobile View Overrides (under 768px) ── */
        @media (max-width: 767px) {
          .mobile-slots-view {
            padding: 1rem 0;
          }

          /* Circular Back Button */
          .mobile-back-btn {
            width: 40px;
            height: 40px;
            border-radius: 50% !important;
            background-color: #ffffff !important;
            border: 1px solid #e9ecef !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            cursor: pointer;
            transition: all 0.2s ease;
            outline: none;
            margin-bottom: 1.5rem;
            padding: 0 !important;
          }

          .mobile-back-btn:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
            transform: translateY(-1px);
          }

          .mobile-back-btn svg {
            color: #006bff;
            font-size: 16px;
          }

          /* Centered Date Header */
          .mobile-date-header {
            margin-bottom: 1.5rem;
          }
          
          .mobile-date-header h4 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1a1a1a;
          }

          .mobile-date-header p {
            font-size: 0.95rem;
            color: #5f6368;
          }

          /* Timezone section */
          .mobile-timezone-container {
            margin-bottom: 1.75rem;
          }

          .mobile-timezone-label {
            font-size: 0.85rem;
            font-weight: 600;
            color: #1a1a1a;
            margin-bottom: 0.25rem;
          }

          .mobile-timezone-selector {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
            color: #1a1a1a;
            font-weight: 500;
            cursor: pointer;
          }

          .mobile-timezone-selector svg {
            color: #5f6368;
          }

          .mobile-timezone-selector .chevron-icon {
            font-size: 0.75rem;
            color: #1a1a1a;
          }

          /* Subtitles */
          .mobile-select-time-title {
            font-size: 1.25rem;
            font-weight: 700;
            color: #1a1a1a;
            margin-bottom: 0.25rem;
          }

          .mobile-duration-label {
            font-size: 0.9rem;
            color: #5f6368;
            margin-bottom: 1.5rem;
          }

          /* Force single-column grid on mobile time slots */
          .mobile-slots-view .time-slots-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
            padding: 0 !important;
          }

          .mobile-time-slot-item {
            width: 100%;
          }

          .mobile-time-slot-row {
            width: 100%;
            display: flex;
            gap: 0.5rem;
          }
        }

        /* ── Custom Confirmation Message ── */
        .custom-confirm-message {
          display: block;
          text-align: center;
          line-height: 1.6;
          color: #6c757d;
          word-break: break-word;
        }

        .custom-confirm-message p {
          margin-bottom: 0.5rem;
        }

        .custom-confirm-message ul,
        .custom-confirm-message ol {
          text-align: left;
          display: inline-block;
          margin: 0.5rem 0;
          padding-left: 1.25rem;
        }

        .custom-confirm-message a {
          color: #006bff;
          text-decoration: underline;
        }

        /* ── PayU fallback Pay Now button ── */
        .payu-fallback-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .payu-pay-now-btn {
          background: linear-gradient(135deg, #006bff 0%, #0056cc 100%) !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          font-size: 1.1rem !important;
          padding: 0.75rem 2.5rem !important;
          border-radius: 8px !important;
          border: none !important;
          box-shadow: 0 4px 14px rgba(0, 107, 255, 0.35) !important;
          transition: all 0.25s ease !important;
        }

        .payu-pay-now-btn:hover {
          background: linear-gradient(135deg, #0056cc 0%, #004bb5 100%) !important;
          box-shadow: 0 6px 20px rgba(0, 107, 255, 0.45) !important;
          transform: translateY(-1px);
        }

        .payu-pay-now-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(0, 107, 255, 0.3) !important;
        }


      `}</style>
    </div>
  );
};

export default Booking;
